// Syft ACP - Core <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import { get } from 'lodash-es'
import PropTypes from 'prop-types'
import React from 'react'

import { Button, Flex, Text, Tooltip } from '@indeed/ifl-components'
import { Information } from '@indeed/ifl-icons'
import * as structureTypes from 'syft-acp-core/components/EditableTable'
import { SUPPORTED_COUNTRY_CODES } from 'syft-acp-core/lib/i18n'
import { getWorkWeekVariants } from 'syft-acp-util/hooks/useWorkWeekVariants'
import './structure.css'

export const dataPropTypes = PropTypes.shape({
  id: PropTypes.number,
  company_name: PropTypes.string,
  contact_name: PropTypes.string,
  contact_mobile: PropTypes.string,
  address: PropTypes.shape({
    city: PropTypes.string,
    line_1: PropTypes.string,
    line_2: PropTypes.string,
    post_code: PropTypes.string,
  }),
  email: PropTypes.string,
  brand_name: PropTypes.string,
  company_number: PropTypes.string,
  vat_number: PropTypes.string,
  summary: PropTypes.string,
  approved_at: PropTypes.string,
  picture: PropTypes.shape({
    uuid: PropTypes.string,
  }),
  skip_minimum_shift_duration: PropTypes.bool,
  marketing_engagement_excluded: PropTypes.bool,
  show_fc_timesheets: PropTypes.bool,
})

const mainData = [
  [
    'id',
    {
      header: 'ID',
    },
  ],
  [
    'brand_name',
    {
      header: 'Brand name',
      isRequired: true,
      editable: true,
    },
  ],
  [
    'company_name',
    {
      header: 'Company name',
      isRequired: true,
      editable: true,
    },
  ],
  [
    'contact_name',
    {
      header: 'Contact name',
      editable: true,
    },
  ],
  [
    'contact_mobile',
    {
      header: 'Contact telephone no.',
      editable: true,
      type: structureTypes.TYPE_PHONE_NUMBER,
    },
  ],
  [
    'email',
    {
      header: 'Registration email',
    },
  ],
  [
    'city_id',
    {
      header: 'City',
      editable: true,
      type: structureTypes.TYPE_CITY_DROPDOWN,
      showForCountries: [SUPPORTED_COUNTRY_CODES.GB],
    },
  ],
  [
    'address.city',
    {
      header: 'City',
      editable: false,
      showForCountries: [SUPPORTED_COUNTRY_CODES.US],
    },
  ],
  [
    'address.federated_state_code',
    {
      header: 'State',
      editable: false,
      type: structureTypes.TYPE_STATE_DROPDOWN,
      showForCountries: [SUPPORTED_COUNTRY_CODES.US],
    },
  ],
  [
    'industry_ids',
    {
      header: 'Industry',
      isRequired: true,
      editable: true,
      type: structureTypes.TYPE_INDUSTRIES_LIST,
    },
  ],
  ['', { type: structureTypes.TYPE_SEPARATOR }],
  [
    'company_number',
    {
      header: 'Company number',
      isRequired: true,
      editable: true,
    },
  ],
  [
    'vat_number',
    {
      header: 'VAT number',
      editable: true,
      showForCountries: [SUPPORTED_COUNTRY_CODES.GB],
    },
  ],
  [
    'vat_number',
    {
      header: 'TAX ID',
      editable: true,
      showForCountries: [SUPPORTED_COUNTRY_CODES.US],
    },
  ],
  [
    'syft_account_manager_id',
    {
      header: 'Account manager',
      editable: true,
      // Hack for syft_account_manager_id. We receive this data from the API as an object
      // named syft_account_manager. But we must send it as syft_account_manager_id.
      // TODO: remove this hack somehow.
      // https://syftapp.atlassian.net/browse/API2-942
      getData: data => get(data, 'syft_account_manager_id') || get(data, 'syft_account_manager.id'),
      parameters: { managerRoles: ['account_manager'] },
      type: structureTypes.TYPE_MANAGER_DROPDOWN,
    },
  ],
  [
    'syft_sales_manager_id',
    {
      header: 'Sales manager',
      isRequired: true,
      editable: true,
      getData: data => get(data, 'syft_sales_manager_id') || get(data, 'syft_sales_manager.id'),
      parameters: { managerRoles: ['sales_manager'] },
      type: structureTypes.TYPE_MANAGER_DROPDOWN,
    },
  ],
  [
    'syft_account_director_id',
    {
      header: 'Account director',
      editable: true,
      getData: data => data?.syft_account_director_id,
      parameters: { managerRoles: ['account_director'] },
      type: structureTypes.TYPE_MANAGER_DROPDOWN,
    },
  ],
  [
    'tier',
    {
      header: 'Tier',
      isRequired: true,
      editable: true,
      type: structureTypes.TYPE_TIER_DROPDOWN,
    },
  ],
  [
    'client_delivery_team_id',
    {
      header: 'Client delivery team',
      isRequired: true,
      editable: true,
      type: structureTypes.TYPE_CLIENT_DELIVERY_TEAM_DROPDOWN,
    },
  ],
  [
    'picture',
    {
      header: 'Photo',
      type: structureTypes.TYPE_IMAGE,
      editable: true,
    },
  ],
  [
    'created_at',
    {
      header: 'Created on',
      type: structureTypes.TYPE_TIMESTAMP,
    },
  ],
  [
    'last_active_at',
    {
      header: 'Last active on',
      type: structureTypes.TYPE_TIMESTAMP,
    },
  ],
  [
    'approved_at',
    {
      header: 'Approved on',
      type: structureTypes.TYPE_TIMESTAMP,
    },
  ],
  [
    'rating.average',
    {
      header: 'Avg. rating by workers',
      type: structureTypes.TYPE_RATING,
    },
  ],
  [
    'approved_at',
    {
      header: 'Verified',
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'referrer_name',
    {
      header: 'Referrer',
      editable: false,
      defaultValue: '–',
    },
  ],
  [
    'allow_reason_for_booking',
    {
      header: 'Enable custom reasons',
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'listing_preset_reasons',
    {
      header: 'Add reasons',
      editable: true,
      type: structureTypes.TYPE_REASONS_ARRAY,
    },
  ],
  [
    'show_booking_reason_on_finance_report',
    {
      header: 'Show reason for booking in finance report',
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'marketing_engagement_excluded',
    {
      header: 'Exclude from Braze comms',
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
]

const editableChecbox = {
  editable: true,
  type: structureTypes.TYPE_CHECKBOX,
}

const regulationsData = [
  [
    'requires_preauth',
    {
      ...editableChecbox,
      header: 'Requires pre-auth',
      showForCountries: [SUPPORTED_COUNTRY_CODES.GB],
    },
  ],
  [
    'no_admin_input',
    {
      ...editableChecbox,
      header: 'No admin input',
      showForCountries: [SUPPORTED_COUNTRY_CODES.US],
    },
  ],
  [
    'skip_minimum_shift_duration',
    {
      ...editableChecbox,
      header: '≤4 hour jobs allowed',
    },
  ],
  [
    'working_time_directive',
    {
      ...editableChecbox,
      header: 'Enforce working time directive',
      showForCountries: [SUPPORTED_COUNTRY_CODES.GB],
    },
  ],
  [
    'auto_no_show_shifts',
    {
      ...editableChecbox,
      header: 'Auto No-Show Shifts',
    },
  ],
  [
    'show_fc_timesheets',
    {
      ...editableChecbox,
      header: 'Show FC Timesheets',
    },
  ],
]

const invoicingData = [
  // FIXME: See CMS2-673
  [
    'invoicing_name',
    {
      header: 'Invoicing client entity name',
      expl: 'Name of the client as you want it to appear on the invoice',
      editable: true,
    },
  ],
  [
    'invoicing_email',
    {
      header: 'Email',
    },
  ],
  [
    'credit_limit',
    {
      header: 'Credit limit',
      editable: true,
    },
  ],
  [
    'credit_terms',
    {
      header: 'Credit terms',
      editable: true,
      type: structureTypes.TYPE_ENUM,
      options: [
        { value: 'NET_0', label: 'Net 0' },
        { value: 'NET_5', label: 'Net 5' },
        { value: 'NET_7', label: 'Net 7' },
        { value: 'NET_10', label: 'Net 10' },
        { value: 'NET_14', label: 'Net 14' },
        { value: 'NET_15', label: 'Net 15' },
        { value: 'NET_21', label: 'Net 21' },
        { value: 'NET_28', label: 'Net 28' },
        { value: 'NET_30', label: 'Net 30' },
        { value: 'NET_31', label: 'Net 31' },
        { value: 'NET_40', label: 'Net 40' },
        { value: 'NET_42', label: 'Net 42' },
        { value: 'NET_45', label: 'Net 45' },
        { value: 'NET_55', label: 'Net 55' },
        { value: 'NET_60', label: 'Net 60' },
        { value: 'NET_70', label: 'Net 70' },
        { value: 'NET_75', label: 'Net 75' },
        { value: 'NET_90', label: 'Net 90' },
        { value: 'CASH', label: 'Cash' },
        { value: 'TWO_PERCENT_10_NET_30', label: '2% 10 Net 30' },
        { value: 'TWENTY_EIGHT_DAYS_END_OF_MONTH', label: '28 Days End of Month' },
        { value: 'THIRTY_DAYS_END_OF_MONTH', label: '30 Days End of Month' },
        { value: 'THIRTY_DAYS_NET_2_PERCENT_10_DAYS', label: '30 Days Net 2% 10 Days' },
        { value: 'FORTY_FIVE_DAYS_END_OF_MONTH', label: '45 Days End of Month' },
        { value: 'SIXTY_DAYS_END_OF_THE_MONTH', label: '60 Days End of the Month' },
      ],
    },
  ],
  [
    'financial_risk',
    {
      header: 'Financial risk',
      editable: true,
      type: structureTypes.TYPE_ENUM,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
    },
  ],
  [
    'invoicing_week_ends_on',
    {
      header: 'Workweek',
      editable: true,
      type: structureTypes.TYPE_ENUM,
      parameters: {
        vertical: true,
      },
      options: getWorkWeekVariants().map(({ range, label }) => ({
        value: range[1],
        label,
        disabled: ![1, 6, 0].includes(range[0]),
      })),
    },
  ],
  [
    'use_cost_centre',
    {
      editable: true,
      translationKey: 'use_cost_centre',
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'add_worker_id_to_finance_reports',
    {
      editable: true,
      translationKey: 'add_worker_id_to_finance_reports',
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'allow_externally_billable_jobs',
    {
      header: (
        <Flex sx={{ alignItems: 'center', mt: -2 }}>
          <Text sx={{ color: 'black', mr: -1 }}>Billable jobs</Text>
          <Tooltip
            delay="400"
            label="This shows up on the client portal's cost summary screen to help determine if the job can be billed"
          >
            <Button iconOnly variant="tertiary" aria-label="More information" sx={{ mx: 1, p: 0 }} size="sm">
              <Information aria-hidden size="sm" />
            </Button>
          </Tooltip>
        </Flex>
      ),
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'invoice_venues_independently',
    {
      header: (
        <Flex sx={{ alignItems: 'center', mt: -2 }}>
          <Text sx={{ color: 'black', mr: -1 }}>Invoice venues independently</Text>
          <Tooltip
            delay="400"
            label="When enabled, new venues created for this employer will default to being invoiced independently. Individual venue settings can still be overridden."
          >
            <Button iconOnly variant="tertiary" aria-label="More information" sx={{ mx: 1, p: 0 }} size="sm">
              <Information aria-hidden size="sm" />
            </Button>
          </Tooltip>
        </Flex>
      ),
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'invoicing_note.body',
    {
      header: 'Invoicing notes',
      editable: true,
      type: structureTypes.TYPE_TEXTAREA,
    },
  ],
]

// PTE-10012: these inputs form an address-shaped cluster, so Chrome offers to "Save address?"
// on page load / employer verification even though nothing was submitted. autoComplete: 'off'
// opts them out of browser autofill. ('off' is the only accessibility-valid disable value —
// a non-standard token fails the axe autocomplete-valid check.)
const noAutofill = { autoComplete: 'off' }

const addressData = [
  ['address.line_1', { header: 'Address line 1', editable: true, parameters: noAutofill }],
  ['address.line_2', { header: 'Address line 2', editable: true, parameters: noAutofill }],
  ['address.post_code', { editable: true, translationKey: 'postcode', parameters: noAutofill }],
  ['address.city', { header: 'City (company office)', editable: true, parameters: noAutofill }],
  [
    'address.federated_state_code',
    {
      header: 'State',
      editable: true,
      type: structureTypes.TYPE_STATE_DROPDOWN,
      showForCountries: [SUPPORTED_COUNTRY_CODES.US],
    },
  ],
]

const agencyPermissions = [
  [
    'enable_agency_job_creation',
    {
      header: 'Enable job posting',
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'enable_agency_cost_centre_code',
    {
      header: 'Enabled cost centre code',
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    'enable_agency_job_approval',
    {
      header: 'Client approval required',
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
]

const granularShiftEdits = [
  [
    'allow_worker_level_edit_venue',
    {
      header: 'Allow Venue edits',
      editable: true,
      type: structureTypes.TYPE_CHECKBOX,
    },
  ],
  [
    '',
    {
      header: null,
      rowOptions: {
        colSpan: 10,
      },
      getData: () => (
        <div className="GranularShiftEdits-text">
          Area, Pay Rate and Role edits are enabled for all clients by default, any issues please reach out to
          R&D support.
        </div>
      ),
    },
  ],
]

export const dataStructure = {
  mainData,
  invoicingData,
  regulationsData,
  addressData,
  agencyPermissions,
  granularShiftEdits,
}
